import React, { useContext, useEffect, useState } from 'react';
import { Menu, FormText, Popover } from '@milanote/roma';
import { OverlayTriggerStateContext } from 'react-aria-components';
import { useDispatch } from 'react-redux';

import { Label } from '../../../../../../../common/labels/labelTypes';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import { deleteLabel } from '../../../../../../../common/labels/labelActions';
import { LabeledElementsData } from '../../../../../../../common/api/labels/labelElementsRetrievalRouteTypes';
import { getLabelUsageData } from '../../../../../../element/labels/labelsService';
import LabelColorPicker from './LabelColorPicker';
import LabelDeleteConfirmationContent from './LabelDeleteConfirmationContent';

type Props = {
    label: Label;
    onRename: () => void;
    onClose: () => void;
};

const LabelDeleteConfirmationPopoverContent: React.FC<{ onDelete: () => void }> = ({ onDelete }) => {
    const deletePopoverState = useContext(OverlayTriggerStateContext);

    return <LabelDeleteConfirmationContent onDelete={onDelete} onCancel={() => deletePopoverState?.close()} />;
};

const LabelListItemActionsPopover: React.FC<Props> = ({ label, onRename, onClose }) => {
    const dispatch = useDispatch();
    const [usageDataState, setUsageDataState] = useState<undefined | 'fetching' | 'success' | 'error'>();
    const [labeledElements, setLabeledElements] = useState<LabeledElementsData[]>([]);
    const translate = useTranslator('popup', 'labels.edit');

    const showUsageData = usageDataState === 'success' || usageDataState === 'error';

    useEffect(() => {
        const getLabelUsage = async () => {
            setUsageDataState('fetching');
            setLabeledElements([]);

            try {
                const labelUsageData = await getLabelUsageData(label._id);
                setUsageDataState('success');
                setLabeledElements(labelUsageData);
            } catch (e) {
                setUsageDataState('error');
            }
        };

        getLabelUsage();
    }, [label._id]);

    const handleDelete = () => {
        dispatch(deleteLabel(label._id));
        onClose();
    };

    return (
        <>
            <Popover.Body>
                <Menu aria-label={`${label.name} actions`} size="sm">
                    <Menu.Item id={`rename-${label._id}`} label={translate('rename')} onAction={onRename} />

                    <Menu.Submenu triggerMode="click">
                        <Menu.Item id={`change-color-${label._id}`} label={translate('changeColor')} />
                        <Menu.SubmenuPopover className="flex">
                            <Popover.Body className="flex h-full items-center justify-center p-4 [--popup-padding:--spacing(4)]">
                                <LabelColorPicker label={label} />
                            </Popover.Body>
                        </Menu.SubmenuPopover>
                    </Menu.Submenu>

                    <Menu.Submenu triggerMode="click">
                        <Menu.Item id={`delete-${label._id}`} label={translate('delete')} destructive />
                        <Menu.SubmenuPopover className="w-[220px]">
                            <Popover.Body className="py-2xl px-2xl">
                                <LabelDeleteConfirmationPopoverContent onDelete={handleDelete} />
                            </Popover.Body>
                        </Menu.SubmenuPopover>
                    </Menu.Submenu>
                </Menu>
            </Popover.Body>

            <Menu.Footer>
                <FormText size="sm" color="quaternary">
                    {showUsageData ? (
                        translate(
                            'used',
                            {
                                count: labeledElements.length,
                                context: usageDataState === 'error' ? 'error' : undefined,
                            },
                            {
                                components: {
                                    usageLink: <a href="#" onClick={(e) => e.preventDefault()} />,
                                },
                            },
                        )
                    ) : (
                        <span>&nbsp;</span>
                    )}
                    {/* 👆 loading state TBC */}
                </FormText>
            </Menu.Footer>
        </>
    );
};

export default LabelListItemActionsPopover;
